import type {
  AiCoach_Action as AiCoachAction,
  AiCoach_CourseraContextInput as AiCoachCourseraContextInput,
} from '__generated__/graphql-types';
import { AiCoach_Mode as AiCoachMode } from '__generated__/graphql-types';

import { stringKeyToTuple, tupleToStringKey } from 'js/lib/stringKeyTuple';
import { get as getUser } from 'js/lib/user';

import { captureMessageInSentry } from 'bundles/ai-course-coach/utils/sentryUtils';
import { COACH_ITEM_DIALOGUE, COACH_ITEM_ROLE_PLAY } from 'bundles/author-course/constants/ItemTypes';
import type Item from 'bundles/author-course/models/Item';
import {
  COACH_ITEM_TYPENAME_OVERRIDE,
  CURRENT_ROLE_PLAY_PROMPT_VERSION,
  CURRENT_SOCRATIC_DIALOGUE_PROMPT_VERSION,
  type CoachItemTypeOverride,
  ROLE_PLAY_PLUGIN_NAME,
  RolePlayModality,
  SOCRATIC_DIALOGUE_PLUGIN_NAME,
} from 'bundles/coach-item/common/constants';
import type { CoachItemPluginName } from 'bundles/coach-item/common/types';
import {
  AiEmbeddingsIndexDocumentErrorCode,
  AiPersonaBehavior,
  AuthoringMode,
  AuthoringPreviewUiMode,
  CoachItemType,
} from 'bundles/coach-item/common/types';
import type { ItemTypeName } from 'bundles/item/types/ItemType';
import { ItemTypeNames } from 'bundles/item/types/ItemType';
import type { Item as LearnerProgressItem } from 'bundles/learner-progress/types/Item';

import _t from 'i18n!nls/coach-item';

// This is still used in `AuthorWidgetCoachItem`, which is necessary for viewing past versions of coach items that were migrated from plugins.
export const getCoachItemTypeFromPlugin = (pluginName: CoachItemPluginName): CoachItemType => {
  if (pluginName === SOCRATIC_DIALOGUE_PLUGIN_NAME) {
    return CoachItemType.SocraticDialogue;
  }

  if (pluginName === ROLE_PLAY_PLUGIN_NAME) {
    return CoachItemType.RolePlay;
  }

  captureMessageInSentry({
    error: new Error(`Unknown coach item plugin name: ${pluginName}`),
    source: 'getCoachItemTypeFromPlugin',
    coachMode: AiCoachMode.CoachItem,
  });

  throw new Error(`Unknown coach item plugin name: ${pluginName}`);
};

export const getCoachSpecificItemFromItem = (item: Item) => {
  if (item.customDisplayTypenameOverride === COACH_ITEM_TYPENAME_OVERRIDE.ROLE_PLAY) {
    return COACH_ITEM_ROLE_PLAY;
  }

  return COACH_ITEM_DIALOGUE;
};

export const hasCoachItemTypeOverride = (
  customDisplayTypenameOverride: string | undefined
): customDisplayTypenameOverride is CoachItemTypeOverride => {
  switch (customDisplayTypenameOverride) {
    case COACH_ITEM_TYPENAME_OVERRIDE.DIALOGUE:
      return true;
    case COACH_ITEM_TYPENAME_OVERRIDE.ROLE_PLAY:
      return true;
    default:
  }
  return false;
};

export const getCoachItemVariant = (
  customDisplayTypenameOverride: CoachItemTypeOverride
): typeof ItemTypeNames.dialogue | typeof ItemTypeNames.rolePlay => {
  switch (customDisplayTypenameOverride) {
    case COACH_ITEM_TYPENAME_OVERRIDE.DIALOGUE:
      return ItemTypeNames.dialogue;
    case COACH_ITEM_TYPENAME_OVERRIDE.ROLE_PLAY:
      return ItemTypeNames.rolePlay;
    default:
      throw new Error(`Could not get Coach Item variant from override: ${customDisplayTypenameOverride}`);
  }
};

export const getCoachItemTypeFromLearnerProgressItem = (item: LearnerProgressItem): ItemTypeName => {
  if (item.customDisplayTypenameOverride === COACH_ITEM_TYPENAME_OVERRIDE.ROLE_PLAY) {
    return ItemTypeNames.rolePlay;
  }

  return ItemTypeNames.dialogue;
};

export const getCoachItemTypeNameOverride = (computedItem: LearnerProgressItem): 'dialogue' | undefined => {
  if (computedItem.contentSummary.typeName === 'ungradedWidget') {
    const { iframeTitle } = computedItem.contentSummary.definition;
    if (iframeTitle === SOCRATIC_DIALOGUE_PLUGIN_NAME) {
      return 'dialogue';
    }
  }

  return undefined;
};

export const versionedCoachItemType = (coachItemType: CoachItemType, promptVersion: number) => {
  return `${coachItemType}@${promptVersion}`;
};

export const unversionedCoachItemType = (versionedType: string) => {
  return versionedType.split('@')[0] as CoachItemType;
};

/** Create a random string of unreliable length */
export const randomString = () => Math.random().toString(36).slice(2);

export const getCoachItemSessionTitle = (courseId: string, itemId: string, inAuthoringMode: boolean) => {
  return `${inAuthoringMode ? 'authoring-' : ''}coachItem-${courseId}-${itemId}`;
};

export const getRolePlayIntroSessionTitle = (courseId: string, itemId: string, inAuthoringMode: boolean) => {
  return tupleToStringKey([inAuthoringMode ? 'authoring-coachItem' : 'coachItem', courseId, itemId, 'intro']);
};

export const getRolePlaySessionTitle = (
  courseId: string,
  itemId: string,
  introSessionId: string,
  modality: (typeof RolePlayModality)[keyof typeof RolePlayModality],
  inAuthoringMode: boolean
) => {
  return tupleToStringKey([
    inAuthoringMode ? 'authoring-coachItem' : 'coachItem',
    courseId,
    itemId,
    'rolePlay',
    modality,
    introSessionId,
  ]);
};

export const getLegacyRolePlaySessionTitle = (
  courseId: string,
  itemId: string,
  introSessionId: string,
  inAuthoringMode: boolean
) => {
  return tupleToStringKey([
    inAuthoringMode ? 'authoring-coachItem' : 'coachItem',
    courseId,
    itemId,
    'rolePlay',
    introSessionId,
  ]);
};

/**
 * Parses a role play simulation session title into structured fields.
 * Defaults modality to `RolePlayModality.TEXT` if it was not recorded as part of the session.
 *
 * The input is a serialized tuple produced by `tupleToStringKey`, so `stringKeyToTuple`
 * returns the parts in order.
 *
 * Supported formats for the serialized session title string:
 * - New:    coachItem~courseId~itemId~rolePlay~{modality}~introSessionId
 * - Legacy: coachItem~courseId~itemId~rolePlay~introSessionId
 * - Authoring uses "authoring-coachItem" as the prefix.
 *
 * @param sessionTitle Serialized role play simulation session title string.
 * @returns Parsed fields, or null if the title is not a role play simulation title.
 */
export const parseRolePlaySimulationSessionTitle = (
  sessionTitle: string
): {
  courseId: string;
  itemId: string;
  introSessionId: string;
  modality: (typeof RolePlayModality)[keyof typeof RolePlayModality];
} | null => {
  const tuple = stringKeyToTuple(sessionTitle);

  if (tuple.length < 5) {
    return null;
  }

  const [prefix, courseId, itemId, rolePlayToken, maybeModalityOrIntroSessionId, maybeIntroSessionId] = tuple;

  if (!courseId || !itemId || !rolePlayToken || !maybeModalityOrIntroSessionId) {
    return null;
  }

  if ((prefix !== 'coachItem' && prefix !== 'authoring-coachItem') || rolePlayToken !== 'rolePlay') {
    return null;
  }

  if (tuple.length >= 6) {
    if (!maybeIntroSessionId) {
      return null;
    }

    if (
      maybeModalityOrIntroSessionId === RolePlayModality.TEXT ||
      maybeModalityOrIntroSessionId === RolePlayModality.VOICE
    ) {
      return {
        courseId,
        itemId,
        introSessionId: maybeIntroSessionId,
        modality: maybeModalityOrIntroSessionId,
      };
    }
    return null;
  }

  // Legacy format without modality encoded in the title.
  return {
    courseId,
    itemId,
    introSessionId: maybeModalityOrIntroSessionId,
    modality: RolePlayModality.TEXT,
  };
};

export const getRolePlayDebriefSessionTitle = (
  courseId: string,
  itemId: string,
  rolePlaySessionId: string,
  inAuthoringMode: boolean
) => {
  return tupleToStringKey([
    inAuthoringMode ? 'authoring-coachItem' : 'coachItem',
    courseId,
    itemId,
    'debrief',
    rolePlaySessionId,
  ]);
};

export const getRolePlayHintsSessionTitle = (
  courseId: string,
  itemId: string,
  rolePlaySessionId: string,
  inAuthoringMode: boolean
) => {
  return tupleToStringKey([
    inAuthoringMode ? 'authoring-coachItem' : 'coachItem',
    courseId,
    itemId,
    'hints',
    rolePlaySessionId,
  ]);
};

export const getRolePlayTasksSessionTitle = (
  courseId: string,
  itemId: string,
  rolePlaySessionId: string,
  inAuthoringMode: boolean
) => {
  return tupleToStringKey([
    inAuthoringMode ? 'authoring-coachItem' : 'coachItem',
    courseId,
    itemId,
    'tasks',
    rolePlaySessionId,
  ]);
};

interface DetermineAuthoringPreviewUiModeArgs {
  uploaderHasError: boolean;
  inputErrors?: Record<string, boolean>;
  validationDetermined?: boolean;
  isPreviousVersion?: boolean;
}
export const determineAuthoringPreviewUiMode = ({
  uploaderHasError,
  inputErrors,
  validationDetermined = true,
  isPreviousVersion,
}: DetermineAuthoringPreviewUiModeArgs): AuthoringPreviewUiMode => {
  if (!validationDetermined) {
    return AuthoringPreviewUiMode.LOADING;
  }

  if (Object.values(inputErrors || {}).filter((hasError) => hasError).length > 0) {
    return AuthoringPreviewUiMode.MISSING_INFO;
  }

  if (uploaderHasError) {
    return AuthoringPreviewUiMode.ERROR;
  }

  if (isPreviousVersion) {
    return AuthoringPreviewUiMode.PREVIOUS_VERSION;
  }

  return AuthoringPreviewUiMode.ACTIVE;
};

export const authoringModeToAiCoachMode = (authoringMode: AuthoringMode | undefined): AiCoachMode => {
  switch (authoringMode) {
    case AuthoringMode.DRAFT:
      return AiCoachMode.CoachItemAuthoringDraft;
    case AuthoringMode.PUBLISHED:
      return AiCoachMode.CoachItemAuthoringPublished;
    default:
      return AiCoachMode.CoachItem;
  }
};

export const getDefaultDialogueAuthoringItemTitle = () => _t('Untitled');

export const stringIsAiPersonaBehavior = (str: string | undefined): str is AiPersonaBehavior => {
  return !!str && Object.values(AiPersonaBehavior).includes(str);
};

export const getAiPersonaBehaviorDescription = (aiPersonaBehavior: AiPersonaBehavior): string => {
  switch (aiPersonaBehavior) {
    case AiPersonaBehavior.Cooperative:
      return _t('Cooperative - Acts warm, reassuring, and optimistic.');
    case AiPersonaBehavior.Skeptical:
      return _t('Skeptical - Acts friendly, assertive, and slightly pessimistic.');
    case AiPersonaBehavior.Demanding:
      return _t(
        'Demanding (Use with caution) - Acts disagreeable, hard to persuade, and is less likely to help the learner.'
      );
    default:
      return '';
  }
};

export const getCoachItemPromptVersion = (itemType: CoachItemType): number => {
  switch (itemType) {
    case CoachItemType.SocraticDialogue:
      return CURRENT_SOCRATIC_DIALOGUE_PROMPT_VERSION;
    case CoachItemType.RolePlay:
      return CURRENT_ROLE_PLAY_PROMPT_VERSION;
    default:
      throw new Error(`Unknown coach item type: ${itemType}`);
  }
};

export const getUploadErrorString = (errorCodes: AiEmbeddingsIndexDocumentErrorCode) => {
  switch (errorCodes) {
    case AiEmbeddingsIndexDocumentErrorCode.NotFoundError:
      return _t('The specified file could not be found. Please try again.');
    case AiEmbeddingsIndexDocumentErrorCode.SizeLimitExceededError:
      return _t('The file exceeds the maximum allowed size. Please reduce the file size and try again.');
    case AiEmbeddingsIndexDocumentErrorCode.UnsupportedFileTypeError:
      return _t('The file format is not supported. Please upload a file in one of the accepted formats: .txt');
    case AiEmbeddingsIndexDocumentErrorCode.ProcessingError:
    default:
      return _t('Something went wrong. Please try again or contact Coursera support if the issue persists.');
  }
};

type CourseraContextInput = {
  authoringCourseBranchId?: string;
  courseBranchId?: string;
  courseId: string;
  itemId: string;
  authoringMode?: AuthoringMode;
};

export const getCourseraContext = (input: CourseraContextInput) => {
  const { authoringCourseBranchId, authoringMode, courseBranchId, courseId, itemId } = input;

  const courseraContext: AiCoachCourseraContextInput = {
    courseId,
    itemId,
  };

  if (authoringMode !== undefined) {
    courseraContext.authoringCourseBranchId = authoringCourseBranchId;
  }

  if (courseBranchId !== undefined) {
    courseraContext.courseBranchId = courseBranchId;
  }

  return courseraContext;
};

type CreateNewMessageInputType = {
  message: string;
  action: AiCoachAction;
  chatSessionId: string;
  authoringMode: AuthoringMode | undefined;
  courseraContext: AiCoachCourseraContextInput;
};

export const createNewMessageInput = (input: CreateNewMessageInputType) => {
  const { authoringMode, message, action, chatSessionId, courseraContext } = input;

  return {
    input: {
      userId: String(getUser()?.id),
      message,
      courseraContext,
      mode: authoringModeToAiCoachMode(authoringMode),
      action,
      chatSessionId,
    },
    recommendedActionsEnabled: true,
    clipRecommendationsEnabled: false,
  };
};

export const getCourseItemTypeFromCoachItemType = (coachItemType: CoachItemType): 'dialogue' | 'role_play' => {
  switch (coachItemType) {
    case CoachItemType.SocraticDialogue:
      return 'dialogue';
    case CoachItemType.RolePlay:
      return 'role_play';
    default:
      throw new Error(`Unknown coach item type: ${coachItemType}`);
  }
};

/**
 * Check if a string already contains CML markup
 */
export const isStringAlreadyCML = (str: string): boolean => {
  if (!str || typeof str !== 'string') {
    return false;
  }

  return /<co-content>[\s\S]*<\/co-content>/i.test(str);
};

/**
 * Simple wrapper around `captureMessageInSentry()` with CoachItem mode.
 */
export const captureErrorInSentry = ({ error, source }: { error: Error; source: string }) => {
  captureMessageInSentry({ error, source, coachMode: AiCoachMode.CoachItem });
};
